export const CREATOR_SITE_EMBED_PROVIDERS = ['youtube', 'vimeo', 'spotify', 'soundcloud', 'loom'] as const;

export type CreatorSiteEmbedProvider = (typeof CREATOR_SITE_EMBED_PROVIDERS)[number];

export type CreatorSiteEmbedResolution = {
  provider: CreatorSiteEmbedProvider;
  embedUrl: string;
};

const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'youtu.be', 'm.youtube.com']);
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']);
const SPOTIFY_HOSTS = new Set(['open.spotify.com']);
const SOUNDCLOUD_HOSTS = new Set(['soundcloud.com', 'www.soundcloud.com']);
const LOOM_HOSTS = new Set(['loom.com', 'www.loom.com']);

function parseYoutubeId(url: URL): string | null {
  if (url.hostname === 'youtu.be') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    return id ?? null;
  }
  if (url.pathname.startsWith('/embed/')) {
    return url.pathname.split('/')[2] ?? null;
  }
  return url.searchParams.get('v');
}

function parseVimeoId(url: URL): string | null {
  const parts = url.pathname.split('/').filter(Boolean);
  const embedIndex = parts.indexOf('video');
  if (embedIndex >= 0 && parts[embedIndex + 1]) return parts[embedIndex + 1] ?? null;
  return parts[parts.length - 1] ?? null;
}

export function resolveCreatorSiteEmbedUrl(rawUrl: string): CreatorSiteEmbedResolution | null {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl.trim());
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;

  const host = parsed.hostname.toLowerCase();

  if (YOUTUBE_HOSTS.has(host)) {
    const id = parseYoutubeId(parsed);
    if (!id) return null;
    return { provider: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${id}` };
  }

  if (VIMEO_HOSTS.has(host)) {
    const id = parseVimeoId(parsed);
    if (!id) return null;
    return { provider: 'vimeo', embedUrl: `https://player.vimeo.com/video/${id}` };
  }

  if (SPOTIFY_HOSTS.has(host) && parsed.pathname.includes('/embed/')) {
    return { provider: 'spotify', embedUrl: parsed.toString() };
  }
  if (SPOTIFY_HOSTS.has(host)) {
    const path = parsed.pathname.replace(/^\/+/, '');
    if (!path) return null;
    return { provider: 'spotify', embedUrl: `https://open.spotify.com/embed/${path}` };
  }

  if (SOUNDCLOUD_HOSTS.has(host)) {
    return {
      provider: 'soundcloud',
      embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(parsed.toString())}`,
    };
  }

  if (LOOM_HOSTS.has(host)) {
    const shareId = parsed.pathname.split('/').filter(Boolean).pop();
    if (!shareId) return null;
    return { provider: 'loom', embedUrl: `https://www.loom.com/embed/${shareId}` };
  }

  return null;
}

export function validateCreatorSiteEmbedContent(
  content: Record<string, unknown>,
): { ok: true; resolution: CreatorSiteEmbedResolution; title: string | null } | { ok: false; message: string } {
  const url = typeof content.url === 'string' ? content.url.trim() : '';
  if (!url) return { ok: false, message: 'embed url is required' };
  const resolution = resolveCreatorSiteEmbedUrl(url);
  if (!resolution) {
    return { ok: false, message: 'embed url must be from an allowed provider (YouTube, Vimeo, Spotify, SoundCloud, Loom)' };
  }
  const title =
    typeof content.title === 'string' && content.title.trim().length > 0
      ? content.title.trim().slice(0, 120)
      : null;
  return { ok: true, resolution, title };
}
